"use client";

import { useCallback, useEffect, useState } from "react";
import Script from "next/script";
import { MOT_COOKIE_PREFERENCES_CHANGED } from "@/components/CookieConsent";
import { isAnalyticsAllowed } from "@/lib/analytics-consent";
import { captureUtmFromLocation, readStoredUtm } from "@/lib/analytics-utm";
import {
  buildGa4InlineInitScript,
  initOrRefreshGa4,
} from "@/lib/ga4-consent";
import { isProdPublicTrackingEnabled } from "@/lib/public-deploy-env";
import { onAnalyticsAllowanceChanged } from "@/lib/vendor-tracking-lifecycle";

/**
 * Loads GA4 only on production deploys and only when analytics is allowed (consent ∧ ¬GPC).
 */
export function Ga4Loader({ measurementId }: { measurementId: string | undefined }) {
  const [enabled, setEnabled] = useState(false);
  const [injectScripts, setInjectScripts] = useState(false);
  const prod = isProdPublicTrackingEnabled();
  const id = measurementId?.trim() ?? "";
  const validId = /^G-[A-Z0-9]+$/i.test(id);

  const applyAllowance = useCallback((allowed: boolean) => {
    if (!allowed) {
      onAnalyticsAllowanceChanged(false);
      setEnabled(false);
      setInjectScripts(false);
      return;
    }
    captureUtmFromLocation(window.location.href);
    setEnabled(true);
  }, []);

  const sync = useCallback(() => {
    applyAllowance(isAnalyticsAllowed());
  }, [applyAllowance]);

  useEffect(() => {
    if (!prod || !validId) return;
    sync();
    function onPrefs() {
      applyAllowance(isAnalyticsAllowed());
    }
    window.addEventListener(MOT_COOKIE_PREFERENCES_CHANGED, onPrefs);
    return () => window.removeEventListener(MOT_COOKIE_PREFERENCES_CHANGED, onPrefs);
  }, [prod, validId, sync, applyAllowance]);

  useEffect(() => {
    if (!enabled || !validId) {
      setInjectScripts(false);
      return;
    }
    captureUtmFromLocation(window.location.href);
    const utm = readStoredUtm();
    if (initOrRefreshGa4(id, utm)) {
      setInjectScripts(false);
    } else {
      setInjectScripts(true);
    }
  }, [enabled, validId, id]);

  if (!prod || !validId || !enabled) {
    return null;
  }

  if (!injectScripts) {
    return null;
  }

  const inlineInit = buildGa4InlineInitScript(id, readStoredUtm());

  return (
    <>
      <Script
        src={`https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`}
        strategy="afterInteractive"
      />
      <Script id={`mot-ga4-init-${id}`} strategy="afterInteractive">
        {inlineInit}
      </Script>
    </>
  );
}
